import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api, API } from '../App';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowLeft, FileText, Truck, Receipt, Calendar, Download, Trash2, RotateCcw, RefreshCw, Plus, Undo2, Edit, Link2, DollarSign } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format } from 'date-fns';

// Standard byproduct categories
const STANDARD_BYPRODUCTS = ['Bran', 'Germ', 'Unga Baridi', 'Others'];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnAdjustmentDialogOpen, setReturnAdjustmentDialogOpen] = useState(false);
  const [deliveryNoteDialogOpen, setDeliveryNoteDialogOpen] = useState(false);
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  // Return/Adjustment Modal State
  const [returnAdjustmentDate, setReturnAdjustmentDate] = useState(new Date());
  const [returnAdjustmentData, setReturnAdjustmentData] = useState({
    amount: '',
    reason: 'Return',
    remarks: ''
  });
  // Phase 4: product-aware return rows per order item
  const [returnItemRows, setReturnItemRows] = useState([]);
  const [orderReturnsAdjustments, setOrderReturnsAdjustments] = useState([]);
  const [deliveryNoteData, setDeliveryNoteData] = useState({
    vehicle: '',
    driver: '',
    notes: ''
  });
  
  // Invoice data for editable modal
  const [invoiceData, setInvoiceData] = useState({
    due_date: '',
    payment_instructions: '',
    payment_terms: '',
    custom_notes: ''
  });
  const [invoiceDueDate, setInvoiceDueDate] = useState(null);
  
  // Dispatch state
  const [dispatchItems, setDispatchItems] = useState([]);
  const [dispatchByproducts, setDispatchByproducts] = useState([]);
  const [dispatchNotes, setDispatchNotes] = useState('');
  const [finishedGoods, setFinishedGoods] = useState([]);
  const [byproductInventory, setByproductInventory] = useState([]);
  
  // Returns & Replacements state
  const [returns, setReturns] = useState([]);
  const [replacements, setReplacements] = useState([]);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [replacementDialogOpen, setReplacementDialogOpen] = useState(false);
  const [packSizes, setPackSizes] = useState([]);
  
  // Return form
  const [returnDate, setReturnDate] = useState(new Date());
  const [returnItems, setReturnItems] = useState([]);
  const [returnRemarks, setReturnRemarks] = useState('');
  const [returnBatchNo, setReturnBatchNo] = useState('');  // NEW: Optional batch for traceability
  
  // Replacement form
  const [replacementDate, setReplacementDate] = useState(new Date());
  const [replacementItems, setReplacementItems] = useState([]);
  const [replacementRemarks, setReplacementRemarks] = useState('');

  // Order Expenses state
  const [orderExpenses, setOrderExpenses] = useState([]);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [expenseData, setExpenseData] = useState({ amount: '', notes: '' });

  // Order Collections (Informational Only) state
  const [orderCollections, setOrderCollections] = useState([]);
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [editingCollectionId, setEditingCollectionId] = useState(null);
  const [collectionDate, setCollectionDate] = useState(new Date());
  const [collectionData, setCollectionData] = useState({
    amount: '',
    method: 'Cash',
    transaction_code: '',
    remarks: ''
  });

  // Utility function
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-KE').format(num || 0);
  };

  useEffect(() => {
    fetchOrder();
    fetchPackSizes();
    fetchInventory();
  }, [id]);
  
  useEffect(() => {
    if (order) {
      fetchReturnsAndReplacements();
      fetchOrderExpenses();
      fetchOrderReturnsAdjustments();
      fetchOrderCollections();
      // Check if should open dispatch modal
      if (searchParams.get('dispatch') === 'true') {
        initializeDispatchForm();
        setDispatchDialogOpen(true);
      }
    }
  }, [order?.id]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/orders/${id}`);
      setOrder(response.data);
      if (response.data.delivery_note_data) {
        setDeliveryNoteData(response.data.delivery_note_data);
      }
    } catch (error) {
      toast.error('Failed to load order');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackSizes = async () => {
    try {
      const response = await api.get('/pack-sizes');
      setPackSizes(response.data);
    } catch (error) {
      console.error('Failed to load pack sizes');
    }
  };

  const fetchInventory = async () => {
    try {
      const [fgRes, bpRes] = await Promise.all([
        api.get('/inventory/finished-goods'),
        api.get('/inventory/byproducts')
      ]);
      setFinishedGoods(fgRes.data || []);
      setByproductInventory(bpRes.data || []);
    } catch (error) {
      console.error('Failed to load inventory');
    }
  };

  const fetchReturnsAndReplacements = async () => {
    try {
      const [returnsRes, replacementsRes] = await Promise.all([
        api.get(`/returns?order_id=${id}`),
        api.get(`/replacements?order_id=${id}`)
      ]);
      setReturns(returnsRes.data);
      setReplacements(replacementsRes.data);
    } catch (error) {
      console.error('Failed to load returns/replacements');
    }
  };

  const fetchOrderExpenses = async () => {
    try {
      const res = await api.get(`/orders/${id}/expenses`);
      setOrderExpenses(res.data.expenses || []);
    } catch (error) {
      console.error('Failed to load order expenses');
    }
  };

  const fetchOrderReturnsAdjustments = async () => {
    try {
      const res = await api.get(`/orders/${id}/returns`);
      setOrderReturnsAdjustments(res.data || []);
    } catch (error) {
      console.error('Failed to load order returns/adjustments');
    }
  };

  const fetchOrderCollections = async () => {
    try {
      const res = await api.get(`/orders/${id}/collections`);
      setOrderCollections(res.data || []);
    } catch (error) {
      console.error('Failed to load order collections');
    }
  };

  // Order Collection handlers (informational only - no accounting effect)
  const resetCollectionForm = () => {
    setCollectionData({ amount: '', method: 'Cash', transaction_code: '', remarks: '' });
    setCollectionDate(new Date());
    setEditingCollectionId(null);
  };

  const handleCollectionSubmit = async (e) => {
    e.preventDefault();
    if (!collectionData.amount || parseFloat(collectionData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const payload = {
      date: format(collectionDate, 'yyyy-MM-dd'),
      amount: parseFloat(collectionData.amount),
      method: collectionData.method,
      transaction_code: collectionData.transaction_code || null,
      remarks: collectionData.remarks || null
    };

    try {
      if (editingCollectionId) {
        await api.put(`/orders/${id}/collections/${editingCollectionId}`, payload);
        toast.success('Collection updated');
      } else {
        await api.post(`/orders/${id}/collections`, payload);
        toast.success('Collection recorded');
      }
      resetCollectionForm();
      setCollectionDialogOpen(false);
      fetchOrderCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save collection');
    }
  };

  const handleEditCollection = (collection) => {
    setEditingCollectionId(collection.id);
    setCollectionDate(new Date(collection.date));
    setCollectionData({
      amount: collection.amount.toString(),
      method: collection.method || 'Cash',
      transaction_code: collection.transaction_code || '',
      remarks: collection.remarks || ''
    });
    setCollectionDialogOpen(true);
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm('Delete this collection record?')) return;
    try {
      await api.delete(`/orders/${id}/collections/${collectionId}`);
      toast.success('Collection deleted');
      fetchOrderCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Close/Reopen order handlers
  const handleCloseOrder = async () => {
    if (!window.confirm('Mark this order as closed?')) return;
    try {
      await api.post(`/orders/${id}/close`);
      toast.success('Order closed');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close order');
    }
  };

  const handleReopenOrder = async () => {
    if (!window.confirm('Reopen this order?')) return;
    try {
      await api.post(`/orders/${id}/reopen`);
      toast.success('Order reopened');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen order');
    }
  };

  const initializeDispatchForm = () => {
    if (!order) return;
    
    // SIMPLIFIED: Just track quantities to dispatch (no batch selection)
    const items = order.items?.map(item => ({
      item_id: item.id,
      pack_size_id: item.pack_size_id,
      pack_size_kg: item.pack_size_kg,
      total_bales: item.bales,
      dispatched_bales: item.bales_dispatched || 0,
      remaining_bales: item.bales - (item.bales_dispatched || 0),
      dispatch_bales: 0  // Quantity to dispatch
    })) || [];
    
    const bps = order.byproducts?.map(bp => ({
      byproduct_id: bp.id,
      product: bp.product,
      total_kg: bp.kg,
      dispatched_kg: bp.kg_dispatched || 0,
      remaining_kg: bp.kg - (bp.kg_dispatched || 0),
      dispatch_kg: 0  // Quantity to dispatch
    })) || [];
    
    setDispatchItems(items);
    setDispatchByproducts(bps);
    setDispatchNotes('');
  };

  // Update dispatch quantity for an item
  const updateDispatchItem = (itemIdx, bales) => {
    setDispatchItems(dispatchItems.map((item, idx) => 
      idx === itemIdx ? { ...item, dispatch_bales: parseInt(bales) || 0 } : item
    ));
  };

  // Update dispatch quantity for a byproduct
  const updateDispatchByproduct = (bpIdx, kg) => {
    setDispatchByproducts(dispatchByproducts.map((bp, idx) => 
      idx === bpIdx ? { ...bp, dispatch_kg: parseFloat(kg) || 0 } : bp
    ));
  };

  // Dispatch all remaining
  const dispatchAllRemaining = () => {
    setDispatchItems(dispatchItems.map(item => ({
      ...item,
      dispatch_bales: item.remaining_bales
    })));
    setDispatchByproducts(dispatchByproducts.map(bp => ({
      ...bp,
      dispatch_kg: bp.remaining_kg
    })));
  };

  const handleDispatch = async (e) => {
    e.preventDefault();
    
    // SIMPLIFIED: Build dispatch request with quantities only (batch_no = 'MANUAL')
    const itemsToDispatch = dispatchItems
      .filter(item => item.dispatch_bales > 0)
      .map(item => ({
        item_id: item.item_id,
        allocations: [{ batch_no: 'MANUAL', bales: item.dispatch_bales }]
      }));
    
    const byproductsToDispatch = dispatchByproducts
      .filter(bp => bp.dispatch_kg > 0)
      .map(bp => ({
        byproduct_id: bp.byproduct_id,
        allocations: [{ batch_no: 'MANUAL', kg: bp.dispatch_kg }]
      }));
    
    if (itemsToDispatch.length === 0 && byproductsToDispatch.length === 0) {
      toast.error('Please enter quantities to dispatch');
      return;
    }
    
    // Validate quantities don't exceed remaining
    for (const item of dispatchItems) {
      if (item.dispatch_bales > item.remaining_bales) {
        toast.error(`${item.pack_size_kg}kg: Cannot dispatch ${item.dispatch_bales}, only ${item.remaining_bales} remaining`);
        return;
      }
    }
    for (const bp of dispatchByproducts) {
      if (bp.dispatch_kg > bp.remaining_kg) {
        toast.error(`${bp.product}: Cannot dispatch ${bp.dispatch_kg}kg, only ${bp.remaining_kg}kg remaining`);
        return;
      }
    }
    
    try {
      await api.post(`/orders/${id}/dispatch`, {
        items: itemsToDispatch,
        byproducts: byproductsToDispatch,
        notes: dispatchNotes || null
      });
      toast.success('Items dispatched successfully!');
      setDispatchDialogOpen(false);
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispatch');
    }
  };

  const handleUndoDispatch = async () => {
    if (!window.confirm('This will restore all dispatched inventory. Continue?')) return;
    
    try {
      await api.post(`/orders/${id}/undo-dispatch`);
      toast.success('Dispatch undone - inventory restored');
      fetchOrder();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo dispatch');
    }
  };

  const handleReturnAdjustment = async (e) => {
    e.preventDefault();
    const isReturn = returnAdjustmentData.reason === 'Return';
    if (isReturn) {
      const activeRows = returnItemRows.filter(r => parseFloat(r.return_qty) > 0);
      if (activeRows.length === 0) { toast.error('Enter at least one return quantity'); return; }
      const invalid = activeRows.find(r => parseFloat(r.return_qty) > r.available + 1e-6);
      if (invalid) { toast.error(`Return qty for ${invalid.pack_size_kg}kg exceeds available (${invalid.available})`); return; }
      const items = activeRows.map(r => ({
        order_item_id: r.order_item_id,
        pack_size_kg: parseFloat(r.pack_size_kg),
        quantity: parseFloat(r.return_qty),
        unit_price: parseFloat(r.unit_price),
        return_value: parseFloat((parseFloat(r.return_qty) * parseFloat(r.unit_price)).toFixed(2)),
      }));
      const totalValue = items.reduce((s, x) => s + x.return_value, 0);
      try {
        await api.post(`/orders/${id}/returns`, {
          date: format(returnAdjustmentDate, 'yyyy-MM-dd'),
          amount: totalValue,
          reason: 'Return',
          remarks: returnAdjustmentData.remarks || null,
          items,
        });
        toast.success('Return recorded');
        setReturnAdjustmentDialogOpen(false);
        setReturnAdjustmentData({ amount: '', reason: 'Return', remarks: '' });
        setReturnItemRows([]);
        fetchOrder();
        fetchOrderReturnsAdjustments();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to record return');
      }
      return;
    }
    // Non-Return: keep amount-based flow
    if (!returnAdjustmentData.amount || parseFloat(returnAdjustmentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await api.post(`/orders/${id}/returns`, {
        date: format(returnAdjustmentDate, 'yyyy-MM-dd'),
        amount: parseFloat(returnAdjustmentData.amount),
        reason: returnAdjustmentData.reason,
        remarks: returnAdjustmentData.remarks || null
      });
      toast.success('Adjustment recorded');
      setReturnAdjustmentDialogOpen(false);
      setReturnAdjustmentData({ amount: '', reason: 'Return', remarks: '' });
      fetchOrder();
      fetchOrderReturnsAdjustments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record return/adjustment');
    }
  };

  // Phase 4: preload item rows when dialog opens or reason changes to Return
  useEffect(() => {
    if (!returnAdjustmentDialogOpen || !order) return;
    if (returnAdjustmentData.reason !== 'Return') return;
    const items = order.items || [];
    const priorReturns = orderReturnsAdjustments || [];
    const rows = items.map((it) => {
      let already = 0;
      priorReturns.forEach(pr => (pr.items || []).forEach(ri => {
        if ((ri.order_item_id && it.id && ri.order_item_id === it.id) ||
            (Math.abs(parseFloat(ri.pack_size_kg) - parseFloat(it.pack_size_kg)) < 1e-6)) {
          already += parseFloat(ri.quantity || 0);
        }
      }));
      const dispatched = parseFloat(it.bales || 0);
      return {
        order_item_id: it.id,
        pack_size_kg: it.pack_size_kg,
        dispatched,
        already_returned: already,
        available: Math.max(0, dispatched - already),
        unit_price: parseFloat(it.price_per_bale || 0),
        return_qty: '',
      };
    });
    setReturnItemRows(rows);
  }, [returnAdjustmentDialogOpen, returnAdjustmentData.reason, order, orderReturnsAdjustments]);

  const handleDeleteReturnAdjustment = async (returnId) => {
    if (!window.confirm('Delete this return/adjustment?')) return;
    try {
      await api.delete(`/orders/${id}/returns/${returnId}`);
      toast.success('Return/Adjustment deleted');
      fetchOrder();
      fetchOrderReturnsAdjustments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDeliveryNote = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/orders/${id}/delivery-note`, deliveryNoteData);
      toast.success('Delivery note saved');
      setDeliveryNoteDialogOpen(false);
      fetchOrder();
      // Download PDF after saving using blob
      handleDownloadDocument('delivery_note');
    } catch (error) {
      toast.error('Failed to update delivery note');
    }
  };

  // Return handlers
  const addReturnItem = () => {
    setReturnItems([...returnItems, {
      id: Date.now().toString(),
      pack_size_id: '',
      pack_size_kg: 0,
      bales: 0,
      reason: ''
    }]);
  };

  const updateReturnItem = (itemId, field, value) => {
    setReturnItems(returnItems.map(item => {
      if (item.id !== itemId) return item;
      const updated = { ...item, [field]: value };
      if (field === 'pack_size_id') {
        const ps = packSizes.find(p => p.id === value);
        updated.pack_size_kg = ps?.size_kg || 0;
      }
      if (field === 'bales') {
        updated.bales = parseInt(value) || 0;
      }
      return updated;
    }));
  };

  const removeReturnItem = (itemId) => {
    setReturnItems(returnItems.filter(i => i.id !== itemId));
  };

  const handleReturn = async (e) => {
    e.preventDefault();
    const validItems = returnItems.filter(i => i.pack_size_id && i.bales > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one return item');
      return;
    }

    try {
      await api.post('/returns', {
        order_id: id,
        date: format(returnDate, 'yyyy-MM-dd'),
        batch_no: returnBatchNo || null,  // Optional batch for traceability
        items: validItems.map(i => ({
          pack_size_id: i.pack_size_id,
          pack_size_kg: i.pack_size_kg,
          bales: i.bales,
          reason: i.reason
        })),
        remarks: returnRemarks
      });
      toast.success('Return recorded (for traceability only - no inventory impact)');
      setReturnDialogOpen(false);
      setReturnItems([]);
      setReturnRemarks('');
      setReturnBatchNo('');
      fetchReturnsAndReplacements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record return');
    }
  };

  // Replacement handlers
  const addReplacementItem = () => {
    setReplacementItems([...replacementItems, {
      id: Date.now().toString(),
      pack_size_id: '',
      pack_size_kg: 0,
      batch_no: '',
      bales: 0
    }]);
  };

  const updateReplacementItem = (itemId, field, value) => {
    setReplacementItems(replacementItems.map(item => {
      if (item.id !== itemId) return item;
      const updated = { ...item, [field]: value };
      if (field === 'pack_size_id') {
        const ps = packSizes.find(p => p.id === value);
        updated.pack_size_kg = ps?.size_kg || 0;
        updated.batch_no = '';
      }
      if (field === 'bales') {
        updated.bales = parseInt(value) || 0;
      }
      return updated;
    }));
  };

  const removeReplacementItem = (itemId) => {
    setReplacementItems(replacementItems.filter(i => i.id !== itemId));
  };

  const handleReplacement = async (e) => {
    e.preventDefault();
    const validItems = replacementItems.filter(i => i.pack_size_id && i.batch_no && i.bales > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one replacement item with batch');
      return;
    }

    try {
      await api.post('/replacements', {
        order_id: id,
        date: format(replacementDate, 'yyyy-MM-dd'),
        items: validItems.map(i => ({
          pack_size_id: i.pack_size_id,
          pack_size_kg: i.pack_size_kg,
          batch_no: i.batch_no,
          bales: i.bales
        })),
        remarks: replacementRemarks
      });
      toast.success('Replacement recorded - stock deducted');
      setReplacementDialogOpen(false);
      setReplacementItems([]);
      setReplacementRemarks('');
      fetchReturnsAndReplacements();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record replacement');
    }
  };

  // Order Expense handlers
  const openExpenseDialog = (expense = null) => {
    if (expense) {
      setEditingExpenseId(expense.id);
      setExpenseData({ amount: expense.amount.toString(), notes: expense.notes });
    } else {
      setEditingExpenseId(null);
      setExpenseData({ amount: '', notes: '' });
    }
    setExpenseDialogOpen(true);
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseData.amount || parseFloat(expenseData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!expenseData.notes?.trim()) {
      toast.error('Please enter notes for this expense');
      return;
    }

    try {
      if (editingExpenseId) {
        await api.put(`/orders/${id}/expenses/${editingExpenseId}`, {
          amount: parseFloat(expenseData.amount),
          notes: expenseData.notes
        });
        toast.success('Expense updated');
      } else {
        await api.post(`/orders/${id}/expenses`, {
          amount: parseFloat(expenseData.amount),
          notes: expenseData.notes
        });
        toast.success('Expense added');
      }
      setExpenseDialogOpen(false);
      setExpenseData({ amount: '', notes: '' });
      setEditingExpenseId(null);
      fetchOrderExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/orders/${id}/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchOrderExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  // Open invoice modal to edit details before generating
  const openInvoiceModal = () => {
    // Default payment instructions
    const defaultPaymentInstructions = `Kindly make payment via:
M-Pesa Paybill: _______ Account: _______
Or Bank Transfer: _______ Account No: _______

Please share the payment confirmation after payment.`;

    // Pre-fill with existing invoice data if available
    if (order?.invoice_data) {
      setInvoiceData({
        ...order.invoice_data,
        payment_instructions: order.invoice_data.payment_instructions || defaultPaymentInstructions
      });
      if (order.invoice_data.due_date) {
        setInvoiceDueDate(new Date(order.invoice_data.due_date));
      } else {
        const defaultDue = new Date(order.date);
        defaultDue.setDate(defaultDue.getDate() + 14);
        setInvoiceDueDate(defaultDue);
      }
    } else {
      // Default due date = 14 days from order date
      const defaultDue = new Date(order.date);
      defaultDue.setDate(defaultDue.getDate() + 14);
      setInvoiceDueDate(defaultDue);
      setInvoiceData({
        due_date: format(defaultDue, 'yyyy-MM-dd'),
        payment_instructions: defaultPaymentInstructions,
        payment_terms: 'Net 14',
        custom_notes: ''
      });
    }
    setInvoiceDialogOpen(true);
  };

  const handleGenerateInvoice = async (e) => {
    e?.preventDefault();
    try {
      // Build invoice data with due date from calendar
      const payload = {
        ...invoiceData,
        due_date: invoiceDueDate ? format(invoiceDueDate, 'yyyy-MM-dd') : null
      };
      const response = await api.post(`/orders/${id}/invoice`, payload);
      toast.success(`Invoice ${response.data.invoice_number} generated`);
      setInvoiceDialogOpen(false);
      fetchOrder();
      // Download PDF after generation using blob
      handleDownloadDocument('invoice');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const handleDownloadDocument = async (type, paymentId = null) => {
    try {
      let endpoint;
      let filename;
      
      if (type === 'invoice') {
        endpoint = `/orders/${id}/invoice/pdf`;
        filename = `Invoice_${order.invoice_number || order.order_number}.pdf`;
      } else if (type === 'delivery_note') {
        endpoint = `/orders/${id}/delivery-note/pdf`;
        filename = `DeliveryNote_${order.delivery_note_number || order.order_number}.pdf`;
      } else if (type === 'receipt') {
        endpoint = paymentId 
          ? `/orders/${id}/receipt/pdf?payment_id=${paymentId}`
          : `/orders/${id}/receipt/pdf`;
        filename = `Receipt_${order.order_number}.pdf`;
      } else if (type === 'order_report') {
        endpoint = `/orders/${id}/report/pdf`;
        filename = `OrderReport_${order.order_number}.pdf`;
      }
      
      toast.info('Generating PDF...');
      
      // Use axios to fetch with auth headers, get blob response
      const response = await api.get(endpoint, { responseType: 'blob' });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type === 'invoice' ? 'Invoice' : type === 'delivery_note' ? 'Delivery Note' : 'Receipt'} downloaded!`);
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(error.response?.data?.detail || `Failed to download ${type}`);
    }
  };

  const handleGenerateReceipt = async () => {
    try {
      const response = await api.post(`/orders/${id}/receipt`);
      toast.success(`Receipt ${response.data.receipt_number} generated`);
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate receipt');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getDispatchStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-slate-100 text-slate-700',
      'Partially Dispatched': 'bg-amber-100 text-amber-700',
      'Dispatched': 'bg-green-100 text-green-700'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status || 'Pending'}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    const colors = {
      'Unpaid': 'bg-red-100 text-red-700',
      'Part Paid': 'bg-amber-100 text-amber-700',
      'Paid': 'bg-emerald-100 text-emerald-700'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status || 'Unpaid'}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  if (!order) {
    return <div className="text-center py-8">Order not found</div>;
  }

  const isPending = order.dispatch_status === 'Pending' || !order.dispatch_status;
  const isPartiallyDispatched = order.dispatch_status === 'Partially Dispatched';
  const isFullyDispatched = order.dispatch_status === 'Dispatched';

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders')} className="shrink-0">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-[#1B4D3E] truncate">
              {order.order_number}
            </h1>
            <div className="flex gap-2 mt-1 flex-wrap">
              {getDispatchStatusBadge(order.dispatch_status)}
              {getPaymentStatusBadge(order.payment_status)}
              {order.is_closed && (
                <Badge className="bg-slate-700 text-white">Closed</Badge>
              )}
              {order.factory && <Badge variant="outline">{order.factory}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Dispatch Button - only if not fully dispatched */}
          {!isFullyDispatched && (
            <Button 
              onClick={() => { initializeDispatchForm(); setDispatchDialogOpen(true); }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Truck className="h-4 w-4 mr-2" /> Dispatch
            </Button>
          )}
          
          {/* Undo Dispatch - only if dispatched */}
          {!isPending && (
            <Button variant="outline" onClick={handleUndoDispatch} className="text-amber-600 border-amber-300">
              <Undo2 className="h-4 w-4 mr-2" /> Undo Dispatch
            </Button>
          )}
          
          {/* Edit Order - only if pending */}
          {isPending && (
            <Button variant="outline" onClick={() => navigate(`/orders/${id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" /> Edit
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Order Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Date</Label>
                <p className="font-medium">{order.date}</p>
              </div>
              <div>
                <Label className="text-slate-500">Customer</Label>
                <p className="font-medium">{order.customer_name}</p>
                {order.customer_phone && <p className="text-sm text-slate-500">{order.customer_phone}</p>}
              </div>
              <div>
                <Label className="text-slate-500">Sales Agent</Label>
                <p className="font-medium">{order.agent_name || '-'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Destination</Label>
                <p className="font-medium">{order.destination || '-'}</p>
              </div>
            </div>

            {/* Order Items */}
            <div className="pt-4 border-t">
              <Label className="text-base font-semibold">Order Items</Label>
              <div className="mt-2 -mx-2 sm:mx-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pack Size</TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="text-right">Dispatched</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead className="text-right">Price/Bale</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.items?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.pack_size_kg}kg</TableCell>
                      <TableCell className="text-right">{item.bales}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">
                        {item.bales_dispatched || 0}
                      </TableCell>
                      <TableCell className="text-right text-amber-600">
                        {item.bales - (item.bales_dispatched || 0)}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(item.price_per_bale)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.line_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </div>

            {/* Byproducts */}
            {order.byproducts?.length > 0 && (
              <div className="pt-4 border-t">
                <Label className="text-base font-semibold">Byproducts</Label>
                <div className="mt-2 -mx-2 sm:mx-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Ordered (kg)</TableHead>
                      <TableHead className="text-right">Dispatched</TableHead>
                      <TableHead className="text-right">Remaining</TableHead>
                      <TableHead className="text-right">Price/kg</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {order.byproducts.map((bp, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{bp.product}</TableCell>
                        <TableCell className="text-right">{bp.kg}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {bp.kg_dispatched || 0}
                        </TableCell>
                        <TableCell className="text-right text-amber-600">
                          {bp.kg - (bp.kg_dispatched || 0)}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(bp.price_per_kg)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(bp.line_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </div>
            )}

            {/* Dispatch History */}
            {order.dispatches?.length > 0 && (
              <div className="pt-4 border-t">
                <Label className="text-base font-semibold">Dispatch History</Label>
                <div className="space-y-2 mt-2">
                  {order.dispatches.map((dispatch, idx) => (
                    <div key={idx} className="p-3 bg-green-50 rounded-lg text-sm">
                      <div className="flex justify-between items-start">
                        <p className="font-medium text-green-800">Dispatched on {dispatch.date}</p>
                        {dispatch.dispatched_by_name && (
                          <span className="text-xs text-slate-500">By: {dispatch.dispatched_by_name}</span>
                        )}
                      </div>
                      {dispatch.items?.map((item, i) => (
                        <p key={i} className="text-green-700">
                          • {item.total_bales || item.bales} bales of {item.pack_size_kg}kg
                        </p>
                      ))}
                      {dispatch.byproducts?.map((bp, i) => (
                        <p key={i} className="text-green-700">
                          • {bp.total_kg || bp.kg}kg {bp.product}
                        </p>
                      ))}
                      {dispatch.notes && (
                        <div className="mt-2 pt-2 border-t border-green-200">
                          <p className="text-xs text-slate-500 font-medium">Notes:</p>
                          <p className="text-sm text-green-800 whitespace-pre-wrap">{dispatch.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Column - Financial Summary */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Financial Summary</CardTitle>
                {order.is_closed ? (
                  <Button variant="outline" size="sm" onClick={handleReopenOrder} className="text-green-600 border-green-200">
                    <RefreshCw className="h-4 w-4 mr-1" /> Reopen
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleCloseOrder} className="text-slate-600">
                    Close Order
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-500">Order Total</span>
                <span className="font-bold" data-testid="fin-order-total">{formatCurrency(order.order_total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Returns/Adjustments</span>
                <span className="font-medium text-red-600" data-testid="fin-returns">
                  -{formatCurrency(orderReturnsAdjustments.reduce((sum, r) => sum + r.amount, 0))}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">Net Value</span>
                <span className="font-bold text-green-600" data-testid="fin-net-value">
                  {formatCurrency(order.order_total - orderReturnsAdjustments.reduce((sum, r) => sum + r.amount, 0))}
                </span>
              </div>
              
              {/* Collections section - Informational Only */}
              <div className="border-t pt-3 mt-3">
                <p className="text-xs text-slate-400 mb-2">Collections (Informational Only)</p>
                <div className="flex justify-between">
                  <span className="text-slate-500">Collections Recorded</span>
                  <span className="font-medium text-blue-600" data-testid="fin-collections">
                    {formatCurrency(orderCollections.reduce((sum, c) => sum + c.amount, 0))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Remaining Informational Balance</span>
                  <span className="font-bold text-slate-700" data-testid="fin-remaining">
                    {formatCurrency(
                      (order.order_total - orderReturnsAdjustments.reduce((sum, r) => sum + r.amount, 0)) 
                      - orderCollections.reduce((sum, c) => sum + c.amount, 0)
                    )}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={() => setReturnAdjustmentDialogOpen(true)} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="record-return-adjustment-btn">
                  <RotateCcw className="h-4 w-4 mr-2" /> Return/Adj
                </Button>
                <Button onClick={() => { resetCollectionForm(); setCollectionDialogOpen(true); }} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="record-collection-btn">
                  <DollarSign className="h-4 w-4 mr-2" /> Collection
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Collections History - Informational Only */}
          {orderCollections.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                  Collections (Info Only)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-400 mb-3">These do not affect agent balances or accounting</p>
                <div className="space-y-2">
                  {orderCollections.map((item) => (
                    <div key={item.id} className="p-3 bg-blue-50 rounded text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-blue-700">{formatCurrency(item.amount)}</p>
                          <p className="text-xs text-slate-500">{item.date} • {item.method}</p>
                          {item.transaction_code && (
                            <p className="text-xs text-slate-400">Code: {item.transaction_code}</p>
                          )}
                          {item.remarks && (
                            <p className="text-xs text-slate-400 break-words">{item.remarks}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditCollection(item)}>
                            <Edit className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCollection(item.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Returns/Adjustments History */}
          {orderReturnsAdjustments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Returns / Adjustments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {orderReturnsAdjustments.map((item, idx) => (
                    <div key={idx} className="p-3 bg-red-50 rounded text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-red-700">-{formatCurrency(item.amount)}</p>
                          <p className="text-xs text-slate-500">{item.date} - {item.reason}</p>
                          {item.remarks && (
                            <p className="text-xs text-slate-400">{item.remarks}</p>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteReturnAdjustment(item.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Documents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {/* Invoice - now opens modal first */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={openInvoiceModal}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {order.invoice_number ? `${order.invoice_number}` : 'Generate Invoice'}
                </Button>
                {order.invoice_number && (
                  <Button variant="ghost" size="icon" onClick={() => handleDownloadDocument('invoice')}>
                    <Download className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {/* Delivery Note */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeliveryNoteDialogOpen(true)}
                >
                  <Truck className="h-4 w-4 mr-2" />
                  {order.delivery_note_number || 'Delivery Note'}
                </Button>
                {order.delivery_note_number && (
                  <Button variant="ghost" size="icon" onClick={() => handleDownloadDocument('delivery_note')}>
                    <Download className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {/* Order Report */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleDownloadDocument('order_report')}
                data-testid="generate-order-report-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate Order Report
              </Button>
            </CardContent>
          </Card>

          {/* Order Expenses */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Order Expenses
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => openExpenseDialog()} data-testid="add-expense-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {orderExpenses.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No expenses recorded for this order</p>
              ) : (
                <div className="space-y-2">
                  {orderExpenses.map((expense) => (
                    <div key={expense.id} className="flex items-center justify-between p-2 bg-slate-50 rounded" data-testid={`expense-${expense.id}`}>
                      <div className="flex-1">
                        <p className="font-medium">KES {formatNumber(expense.amount)}</p>
                        <p className="text-xs text-slate-500">{expense.notes}</p>
                        <p className="text-xs text-slate-400">{expense.created_at}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openExpenseDialog(expense)} data-testid={`edit-expense-${expense.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteExpense(expense.id)} data-testid={`delete-expense-${expense.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 border-t">
                    <p className="text-sm font-semibold text-right">
                      Total: KES {formatNumber(orderExpenses.reduce((sum, e) => sum + e.amount, 0))}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Returns & Replacements */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Returns & Replacements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full" onClick={() => setReturnDialogOpen(true)}>
                <RotateCcw className="h-4 w-4 mr-2" /> Record Return
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setReplacementDialogOpen(true)}>
                <RefreshCw className="h-4 w-4 mr-2" /> Record Replacement
              </Button>
              
              {returns.length > 0 && (
                <div className="mt-4">
                  <Label className="text-sm text-slate-500">Returns ({returns.length})</Label>
                  {returns.map((ret, idx) => (
                    <div key={idx} className="p-2 bg-red-50 rounded text-sm mt-1">
                      <p>{ret.date} - {ret.items?.length} items</p>
                      {ret.batch_no && <p className="text-xs text-slate-500">Batch: {ret.batch_no}</p>}
                    </div>
                  ))}
                </div>
              )}
              
              {replacements.length > 0 && (
                <div className="mt-4">
                  <Label className="text-sm text-slate-500">Replacements ({replacements.length})</Label>
                  {replacements.map((rep, idx) => (
                    <div key={idx} className="p-2 bg-amber-50 rounded text-sm mt-1">
                      <p>{rep.date} - {rep.items?.length} items</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dispatch Dialog - SIMPLIFIED (no batch selection) */}
      <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dispatch Items for {order.order_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDispatch} className="space-y-4">
            <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
              <p className="text-sm text-slate-600">
                Enter quantities to dispatch. This is for record-keeping only.
              </p>
              <Button type="button" variant="outline" size="sm" onClick={dispatchAllRemaining}>
                Dispatch All
              </Button>
            </div>
            
            {/* Finished Goods */}
            {dispatchItems.filter(i => i.remaining_bales > 0).length > 0 && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Finished Goods</Label>
                {dispatchItems.map((item, itemIdx) => {
                  if (item.remaining_bales <= 0) return null;
                  const isOverAllocated = item.dispatch_bales > item.remaining_bales;
                  
                  return (
                    <div key={itemIdx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-semibold text-[#1B4D3E]">{item.pack_size_kg}kg Pack</p>
                          <p className="text-xs text-slate-500">
                            Remaining: <span className="font-bold text-amber-600">{item.remaining_bales}</span> bales
                          </p>
                        </div>
                        <div className="w-32">
                          <Input
                            type="number"
                            min="0"
                            max={item.remaining_bales}
                            value={item.dispatch_bales || ''}
                            onChange={(e) => updateDispatchItem(itemIdx, e.target.value)}
                            placeholder="0"
                            className={isOverAllocated ? 'border-red-500' : ''}
                            data-testid={`dispatch-item-${itemIdx}`}
                          />
                        </div>
                      </div>
                      {isOverAllocated && (
                        <p className="text-xs text-red-600 mt-1">Exceeds remaining quantity</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Byproducts */}
            {dispatchByproducts.filter(bp => bp.remaining_kg > 0).length > 0 && (
              <div className="space-y-3 pt-4 border-t">
                <Label className="text-base font-semibold">Byproducts</Label>
                {dispatchByproducts.map((bp, bpIdx) => {
                  if (bp.remaining_kg <= 0) return null;
                  const isOverAllocated = bp.dispatch_kg > bp.remaining_kg;
                  
                  return (
                    <div key={bpIdx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-semibold text-amber-800">{bp.product}</p>
                          <p className="text-xs text-slate-500">
                            Remaining: <span className="font-bold text-amber-600">{bp.remaining_kg}</span> kg
                          </p>
                        </div>
                        <div className="w-32">
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            max={bp.remaining_kg}
                            value={bp.dispatch_kg || ''}
                            onChange={(e) => updateDispatchByproduct(bpIdx, e.target.value)}
                            placeholder="0"
                            className={isOverAllocated ? 'border-red-500' : ''}
                            data-testid={`dispatch-bp-${bpIdx}`}
                          />
                        </div>
                      </div>
                      {isOverAllocated && (
                        <p className="text-xs text-red-600 mt-1">Exceeds remaining quantity</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Dispatch Notes */}
            <div className="space-y-2 pt-4 border-t">
              <Label>Dispatch Notes (Optional)</Label>
              <Textarea
                value={dispatchNotes}
                onChange={(e) => setDispatchNotes(e.target.value)}
                placeholder="Vehicle number, driver name, delivery location, partial dispatch explanation, special instructions..."
                rows={3}
                data-testid="dispatch-notes-input"
              />
            </div>

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-dispatch-btn">
              <Truck className="h-4 w-4 mr-2" /> Confirm Dispatch
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Return/Adjustment Dialog */}
      <Dialog open={returnAdjustmentDialogOpen} onOpenChange={setReturnAdjustmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Return / Adjustment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReturnAdjustment} className="space-y-4">
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              Returns/Adjustments reduce the order value and the sales agent's outstanding balance.
            </p>
            <div className="space-y-2">
              <Label>Date</Label>
              <DatePickerField date={returnAdjustmentDate} onDateChange={setReturnAdjustmentDate} />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={returnAdjustmentData.reason} onValueChange={(v) => setReturnAdjustmentData({ ...returnAdjustmentData, reason: v })}>
                <SelectTrigger data-testid="return-adjustment-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Return">Return</SelectItem>
                  <SelectItem value="Discount">Discount</SelectItem>
                  <SelectItem value="Adjustment">Adjustment</SelectItem>
                  <SelectItem value="Credit Note">Credit Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {returnAdjustmentData.reason === 'Return' ? (
              <div className="space-y-2" data-testid="return-items-section">
                <Label>Order Items Returned</Label>
                <div className="border rounded overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2">Pack Size</th>
                        <th className="text-right p-2">Dispatched</th>
                        <th className="text-right p-2">Already Returned</th>
                        <th className="text-right p-2">Available</th>
                        <th className="text-right p-2">Return Qty</th>
                        <th className="text-right p-2">Unit Price</th>
                        <th className="text-right p-2">Return Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {returnItemRows.length === 0 ? (
                        <tr><td colSpan={7} className="p-3 text-center text-slate-400 italic">No items on this order</td></tr>
                      ) : returnItemRows.map((r, idx) => {
                        const q = parseFloat(r.return_qty) || 0;
                        const value = q * r.unit_price;
                        const over = q > r.available + 1e-6;
                        return (
                          <tr key={idx} className="border-t" data-testid={`return-item-row-${r.pack_size_kg}`}>
                            <td className="p-2 font-medium">{r.pack_size_kg}kg</td>
                            <td className="p-2 text-right">{r.dispatched}</td>
                            <td className="p-2 text-right">{r.already_returned}</td>
                            <td className={`p-2 text-right ${r.available === 0 ? 'text-slate-400' : ''}`}>{r.available}</td>
                            <td className="p-2 text-right">
                              <Input
                                type="number" min="0" step="0.01"
                                value={r.return_qty}
                                onChange={(e) => {
                                  const rows = [...returnItemRows];
                                  rows[idx] = { ...rows[idx], return_qty: e.target.value };
                                  setReturnItemRows(rows);
                                }}
                                className={`h-7 w-20 text-right text-xs ${over ? 'border-red-500' : ''}`}
                                disabled={r.available === 0}
                                data-testid={`return-qty-input-${r.pack_size_kg}`}
                              />
                            </td>
                            <td className="p-2 text-right">KES {r.unit_price.toLocaleString()}</td>
                            <td className="p-2 text-right font-medium">KES {value.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-amber-50 font-semibold">
                        <td colSpan={6} className="p-2 text-right">Total Return Value:</td>
                        <td className="p-2 text-right" data-testid="return-total-value">
                          KES {returnItemRows.reduce((s, r) => s + ((parseFloat(r.return_qty)||0) * r.unit_price), 0).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Amount (KES) *</Label>
                <Input
                  type="number"
                  value={returnAdjustmentData.amount}
                  onChange={(e) => setReturnAdjustmentData({ ...returnAdjustmentData, amount: e.target.value })}
                  placeholder="Enter amount"
                  data-testid="return-adjustment-amount"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea
                value={returnAdjustmentData.remarks}
                onChange={(e) => setReturnAdjustmentData({ ...returnAdjustmentData, remarks: e.target.value })}
                placeholder="Additional notes"
                data-testid="return-adjustment-remarks"
              />
            </div>
            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" data-testid="return-adjustment-submit">
              {returnAdjustmentData.reason === 'Return' ? 'Record Return' : 'Record Adjustment'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delivery Note Dialog */}
      <Dialog open={deliveryNoteDialogOpen} onOpenChange={setDeliveryNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delivery Note Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDeliveryNote} className="space-y-4">
            <div className="space-y-2">
              <Label>Vehicle Registration</Label>
              <Input
                value={deliveryNoteData.vehicle}
                onChange={(e) => setDeliveryNoteData({ ...deliveryNoteData, vehicle: e.target.value })}
                placeholder="e.g., KBZ 123A"
              />
            </div>
            <div className="space-y-2">
              <Label>Driver Name</Label>
              <Input
                value={deliveryNoteData.driver}
                onChange={(e) => setDeliveryNoteData({ ...deliveryNoteData, driver: e.target.value })}
                placeholder="Driver's name"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={deliveryNoteData.notes}
                onChange={(e) => setDeliveryNoteData({ ...deliveryNoteData, notes: e.target.value })}
                placeholder="Additional delivery notes"
              />
            </div>
            <Button type="submit" className="w-full btn-primary">Save & Generate</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog - Editable fields before generation */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {order.invoice_number ? `Update Invoice ${order.invoice_number}` : 'Generate Invoice'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerateInvoice} className="space-y-4">
            <p className="text-sm text-slate-500">
              Fill in optional details for the invoice. Leave blank to skip.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date</Label>
                <DatePickerField
                  date={invoiceDueDate}
                  onDateChange={(d) => setInvoiceDueDate(d)}
                  placeholder="Select date"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Input
                  value={invoiceData.payment_terms}
                  onChange={(e) => setInvoiceData({ ...invoiceData, payment_terms: e.target.value })}
                  placeholder="e.g., Net 14, COD"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Instructions (Bank/M-Pesa details)</Label>
              <Textarea
                value={invoiceData.payment_instructions}
                onChange={(e) => setInvoiceData({ ...invoiceData, payment_instructions: e.target.value })}
                placeholder="Bank: ABC Bank&#10;Account: 123456789&#10;M-Pesa: 0712345678"
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Custom Notes</Label>
              <Textarea
                value={invoiceData.custom_notes}
                onChange={(e) => setInvoiceData({ ...invoiceData, custom_notes: e.target.value })}
                placeholder="Additional notes for this invoice"
                rows={2}
              />
            </div>
            
            <Button type="submit" className="w-full btn-primary">
              <FileText className="h-4 w-4 mr-2" />
              {order.invoice_number ? 'Update & Download' : 'Generate & Download'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Record Return</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReturn} className="space-y-4">
            <p className="text-sm text-slate-500">
              Returns are recorded for traceability only and do NOT affect inventory.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <DatePickerField date={returnDate} onDateChange={setReturnDate} />
              </div>
              <div className="space-y-2">
                <Label>Batch No (Optional)</Label>
                <Input
                  value={returnBatchNo}
                  onChange={(e) => setReturnBatchNo(e.target.value)}
                  placeholder="For traceability"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Return Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addReturnItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              {returnItems.map((item) => (
                <div key={item.id} className="p-3 bg-red-50 rounded-lg grid grid-cols-4 gap-2">
                  <Select value={item.pack_size_id} onValueChange={(v) => updateReturnItem(item.id, 'pack_size_id', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pack" />
                    </SelectTrigger>
                    <SelectContent>
                      {packSizes.map(ps => (
                        <SelectItem key={ps.id} value={ps.id}>{ps.size_kg}kg</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    value={item.bales || ''}
                    onChange={(e) => updateReturnItem(item.id, 'bales', e.target.value)}
                    placeholder="Bales"
                  />
                  <Input
                    value={item.reason}
                    onChange={(e) => updateReturnItem(item.id, 'reason', e.target.value)}
                    placeholder="Reason"
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeReturnItem(item.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
            
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea value={returnRemarks} onChange={(e) => setReturnRemarks(e.target.value)} />
            </div>
            
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">
              Record Return
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Replacement Dialog */}
      <Dialog open={replacementDialogOpen} onOpenChange={setReplacementDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Record Replacement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReplacement} className="space-y-4">
            <p className="text-sm text-amber-600">
              Replacements WILL deduct from inventory (specified batch).
            </p>
            <div className="space-y-2">
              <Label>Date</Label>
              <DatePickerField date={replacementDate} onDateChange={setReplacementDate} />
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Replacement Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addReplacementItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              {replacementItems.map((item) => {
                const availableBatches = getBatchesForPackSize(item.pack_size_id);
                return (
                  <div key={item.id} className="p-3 bg-amber-50 rounded-lg space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <Select value={item.pack_size_id} onValueChange={(v) => updateReplacementItem(item.id, 'pack_size_id', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Pack Size" />
                        </SelectTrigger>
                        <SelectContent>
                          {packSizes.map(ps => (
                            <SelectItem key={ps.id} value={ps.id}>{ps.size_kg}kg</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={item.batch_no} onValueChange={(v) => updateReplacementItem(item.id, 'batch_no', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Batch" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableBatches.map(b => (
                            <SelectItem key={b.batch_no} value={b.batch_no}>{b.batch_no} ({b.bales})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          value={item.bales || ''}
                          onChange={(e) => updateReplacementItem(item.id, 'bales', e.target.value)}
                          placeholder="Bales"
                        />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeReplacementItem(item.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea value={replacementRemarks} onChange={(e) => setReplacementRemarks(e.target.value)} />
            </div>
            
            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">
              Record Replacement
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Order Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingExpenseId ? 'Edit Expense' : 'Add Order Expense'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleExpenseSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Amount (KES) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={expenseData.amount}
                onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })}
                placeholder="Enter amount"
                data-testid="expense-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes *</Label>
              <Textarea
                value={expenseData.notes}
                onChange={(e) => setExpenseData({ ...expenseData, notes: e.target.value })}
                placeholder="Describe the expense (e.g., transport, loading, etc.)"
                rows={3}
                data-testid="expense-notes-input"
              />
            </div>
            <Button type="submit" className="w-full btn-primary" data-testid="save-expense-btn">
              {editingExpenseId ? 'Update Expense' : 'Add Expense'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Collection Dialog (Informational Only) */}
      <Dialog open={collectionDialogOpen} onOpenChange={(open) => { setCollectionDialogOpen(open); if (!open) resetCollectionForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCollectionId ? 'Edit Collection' : 'Record Collection'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCollectionSubmit} className="space-y-4">
            <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
              This is for informational tracking only. It does NOT affect sales agent balances or accounting.
            </p>
            <div className="space-y-2">
              <Label>Date</Label>
              <DatePickerField date={collectionDate} onDateChange={setCollectionDate} />
            </div>
            <div className="space-y-2">
              <Label>Amount (KES) *</Label>
              <Input
                type="number"
                value={collectionData.amount}
                onChange={(e) => setCollectionData({ ...collectionData, amount: e.target.value })}
                placeholder="Enter amount"
                data-testid="collection-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={collectionData.method} onValueChange={(v) => setCollectionData({ ...collectionData, method: v })}>
                <SelectTrigger data-testid="collection-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Mobile Money (M-Pesa)">Mobile Money (M-Pesa)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Transaction Code</Label>
              <Input
                value={collectionData.transaction_code}
                onChange={(e) => setCollectionData({ ...collectionData, transaction_code: e.target.value })}
                placeholder="M-Pesa code, etc."
                data-testid="collection-code"
              />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea
                value={collectionData.remarks}
                onChange={(e) => setCollectionData({ ...collectionData, remarks: e.target.value })}
                placeholder="Additional notes"
                data-testid="collection-remarks"
              />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="collection-submit">
              {editingCollectionId ? 'Update Collection' : 'Record Collection'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
